import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/request';
import { resolveImageUrl } from '../../constants/depositConstants';
import {
  KYC_CERT_TYPE_OPTIONS,
  KYC_FIELD_LABELS,
  KYC_GENDER_OPTIONS,
  buildCountryOptionKey,
  buildEmptyKycForm,
  buildKycFormFromDetail,
  buildKycSubmitPayload,
  canSubmitKyc,
  findCountryByOptionKey,
  isKycFormComplete,
  normalizeCountryList,
  normalizeRealNameStatus,
  validateKycForm,
} from '../../constants/kycConstants';
import { useTranslation } from '../../i18n/LocaleContext';

export default function KycFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [usedList, setUsedList] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(buildEmptyKycForm());

  const orderedFields = useMemo(() => usedList.filter((key) => KYC_FIELD_LABELS[key]), [usedList]);

  const canSubmit = useMemo(
    () => isKycFormComplete(form, usedList) && !submitting && !uploading,
    [form, usedList, submitting, uploading]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    Promise.all([
      memberApi.getKycConfig(),
      memberApi.listCountries({ splitAreaCode: true }),
      memberApi.getInfo(),
      memberApi.getKycDetail(),
    ])
      .then(([config, countryList, info, detail]) => {
        if (!cancelled) {
          const list = config?.usedList || [];
          const normalizedCountries = normalizeCountryList(countryList);
          const status = normalizeRealNameStatus(info?.realNameStatus || detail?.realNameStatus);

          if (!canSubmitKyc(status)) {
            navigate('/kyc', { replace: true });
            return;
          }

          setUsedList(list);
          setCountries(normalizedCountries);
          setForm(
            detail
              ? buildKycFormFromDetail(detail, normalizedCountries)
              : buildEmptyKycForm(list)
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || t('kyc.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate, t]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      if (field === 'identityImageFront' || field === 'identityImageBack') {
        delete next.image;
      }
      return next;
    });
  }

  function handleCountryChange(event) {
    const selected = findCountryByOptionKey(countries, event.target.value);
    if (!selected) {
      setForm((prev) => ({
        ...prev,
        countryKey: '',
        areaCode: '',
        areaName: '',
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      countryKey: buildCountryOptionKey(selected),
      areaCode: selected.areaCode?.trim() || '',
      areaName: selected.zhName?.trim() || '',
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.country;
      return next;
    });
  }

  async function handleImageUpload(side, file) {
    if (!file) {
      return;
    }
    setUploading(side);
    setError('');
    try {
      const url = await memberApi.uploadOssImage(file);
      const field = side === 'front' ? 'identityImageFront' : 'identityImageBack';
      updateField(field, typeof url === 'string' ? url : url?.url || url?.data || '');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('kyc.uploadFailed'));
    } finally {
      setUploading('');
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const errors = validateKycForm(form, usedList, t);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildKycSubmitPayload(form, usedList);
      await memberApi.submitKyc(payload);
      navigate('/kyc', {
        replace: true,
        state: {
          submitted: true,
          from: location.state?.from,
        },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('kyc.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  function renderField(key) {
    const label = t(`kyc.fields.${key}`);

    switch (key) {
      case 'country':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-country">{label}</label>
            <select
              id="kyc-country"
              className="kyc-form-field__control"
              value={form.countryKey}
              onChange={handleCountryChange}
            >
              <option value="">{t('kyc.selectCountry')}</option>
              {countries.map((item) => (
                <option key={buildCountryOptionKey(item)} value={buildCountryOptionKey(item)}>
                  {item.zhName}
                </option>
              ))}
            </select>
            {fieldErrors.country && <p className="kyc-form-field__error">{fieldErrors.country}</p>}
          </section>
        );
      case 'image':
        return (
          <section key={key} className="kyc-form-field">
            <div className="kyc-form-field__label">{label}</div>
            <div className="kyc-image-uploads">
              <button
                type="button"
                className="kyc-image-upload"
                onClick={() => frontInputRef.current?.click()}
                disabled={uploading === 'front'}
              >
                {form.identityImageFront ? (
                  <img src={resolveImageUrl(form.identityImageFront)} alt="" />
                ) : (
                  <span>{t('kyc.uploadFront')}</span>
                )}
              </button>
              <button
                type="button"
                className="kyc-image-upload"
                onClick={() => backInputRef.current?.click()}
                disabled={uploading === 'back'}
              >
                {form.identityImageBack ? (
                  <img src={resolveImageUrl(form.identityImageBack)} alt="" />
                ) : (
                  <span>{t('kyc.uploadBack')}</span>
                )}
              </button>
            </div>
            <input
              ref={frontInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                handleImageUpload('front', e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <input
              ref={backInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                handleImageUpload('back', e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            {fieldErrors.image && <p className="kyc-form-field__error">{fieldErrors.image}</p>}
          </section>
        );
      case 'birthday':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-birthday">{label}</label>
            <input
              id="kyc-birthday"
              type="date"
              className="kyc-form-field__control"
              value={form.birthday}
              onChange={(e) => updateField('birthday', e.target.value)}
            />
            {fieldErrors.birthday && <p className="kyc-form-field__error">{fieldErrors.birthday}</p>}
          </section>
        );
      case 'gender':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-gender">{label}</label>
            <select
              id="kyc-gender"
              className="kyc-form-field__control"
              value={form.gender}
              onChange={(e) => updateField('gender', e.target.value)}
            >
              <option value="">{t('kyc.selectGender')}</option>
              {KYC_GENDER_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(item.labelKey)}
                </option>
              ))}
            </select>
            {fieldErrors.gender && <p className="kyc-form-field__error">{fieldErrors.gender}</p>}
          </section>
        );
      case 'certType':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-cert-type">{label}</label>
            <select
              id="kyc-cert-type"
              className="kyc-form-field__control"
              value={form.certType}
              onChange={(e) => updateField('certType', e.target.value)}
            >
              <option value="">{t('kyc.selectCertType')}</option>
              {KYC_CERT_TYPE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(item.labelKey)}
                </option>
              ))}
            </select>
            {fieldErrors.certType && <p className="kyc-form-field__error">{fieldErrors.certType}</p>}
          </section>
        );
      case 'realName':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-real-name">{label}</label>
            <input
              id="kyc-real-name"
              type="text"
              className="kyc-form-field__control"
              placeholder={t('kyc.placeholder.realName')}
              value={form.realName}
              onChange={(e) => updateField('realName', e.target.value)}
            />
            {fieldErrors.realName && <p className="kyc-form-field__error">{fieldErrors.realName}</p>}
          </section>
        );
      case 'lastName':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-last-name">{label}</label>
            <input
              id="kyc-last-name"
              type="text"
              className="kyc-form-field__control"
              placeholder={t('kyc.placeholder.lastName')}
              value={form.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
            />
            {fieldErrors.lastName && <p className="kyc-form-field__error">{fieldErrors.lastName}</p>}
          </section>
        );
      case 'identityNumber':
        return (
          <section key={key} className="kyc-form-field">
            <label className="kyc-form-field__label" htmlFor="kyc-identity-number">{label}</label>
            <input
              id="kyc-identity-number"
              type="text"
              className="kyc-form-field__control"
              placeholder={t('kyc.placeholder.identityNumber')}
              value={form.identityNumber}
              onChange={(e) => updateField('identityNumber', e.target.value)}
            />
            {fieldErrors.identityNumber && (
              <p className="kyc-form-field__error">{fieldErrors.identityNumber}</p>
            )}
          </section>
        );
      default:
        return null;
    }
  }

  return (
    <div className="deposit-page deposit-page--form kyc-page">
      <header className="deposit-page__header">
        <button
          type="button"
          className="deposit-page__back"
          aria-label={t('common.back')}
          onClick={() => navigate(-1)}
        >
          ‹
        </button>
        <h1>{t('kyc.formTitle')}</h1>
        <span className="deposit-page__header-spacer" />
      </header>

      {loading && <p className="deposit-page__status">{t('common.loading')}</p>}
      {error && <p className="deposit-page__error">{error}</p>}

      {!loading && (
        <form className="kyc-form" onSubmit={handleSubmit}>
          {orderedFields.map((key) => renderField(key))}
          <button
            type="submit"
            className={`withdraw-page__submit ${canSubmit ? 'is-ready' : ''}`}
            disabled={submitting || Boolean(uploading)}
          >
            {submitting ? t('common.loading') : t('kyc.submit')}
          </button>
        </form>
      )}
    </div>
  );
}
